import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import { reviews } from './review-data';

export default function ReviewDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const review = reviews.find(r => r.slug === slug);

  useEffect(() => {
    if (!review) {
      navigate('/reviews');
    }
  }, [review, navigate]);

  if (!review) {
    return null;
  }

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const currentIndex = reviews.findIndex(r => r.slug === slug);
  const prevReview = currentIndex > 0 ? reviews[currentIndex - 1] : null;
  const nextReview = currentIndex < reviews.length - 1 ? reviews[currentIndex + 1] : null;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Review',
    itemReviewed: {
      '@type': 'Product',
      name: review.title,
      image: review.image,
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: review.rating,
      bestRating: 5,
    },
    author: {
      '@type': 'Person',
      name: review.author,
    },
    datePublished: review.date,
    reviewBody: review.reviewText,
  };

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    const formData = new URLSearchParams();
    formData.append('email', email);

    try {
      const response = await fetch('https://readdy.ai/api/form/d5dbhf1dn6dhfpiac24g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Thank you for subscribing!');
        setEmail('');
      } else {
        setSubmitMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCTATitle = (id: number) => {
    const titles: { [key: number]: string } = {
      1: 'Ready to Transform Your Living Room?',
      2: 'Maximize Your Space with This Futon Sofa Bed',
      3: 'Bring Cultural Elegance to Your Home',
      4: 'Upgrade Your Bathroom Storage Today',
      5: 'Upgrade Your Bathroom with This LED Mirror',
      6: 'Elevate Your Bath Experience Today',
      7: 'Experience Spa-Quality Comfort at Home',
      8: 'Elevate Your Living Room with Premium Oak & Glass',
      9: 'Transform Your Living Space with Smart Storage',
      10: 'Equip Your Kitchen with Professional-Grade Knives',
      11: 'Add This Waterproof, UV-Resistant Canvas Wall Art with Elegant Black Frame to Your Home',
      12: 'Experience High-Definition Giclee Printing on Waterproof Canvas with Professional Framing',
      13: 'Get the Govee Floor Lamp 2 with RGBICWW Technology and Voice Control Capabilities',
      14: 'Get This Powerful 1700W Air Fryer with 8-in-1 Versatility and Stylish Design',
      15: 'Organize Your Bathroom with Smart Storage',
      30: 'Upgrade Your Home Office with This Electric Standing Desk',
      31: 'Transform Your Bathroom with This Complete Sage Green Set',
      32: 'Organize Your Entryway with This USA-Made Coat Rack with Shelf',
      33: 'Sleep in Luxury Every Night with the Serta Goose Feather Down Comforter',
    };
    return titles[id] || 'Get This Product Today';
  };

  const getCTADescription = (id: number) => {
    const descriptions: { [key: number]: string } = {
      1: 'Get this modular sectional and experience ultimate comfort and flexibility',
      2: 'Transform your small space with this versatile 4-in-1 convertible futon sofa bed',
      3: 'Experience authentic handmade craftsmanship with this unique oriental floor seating set',
      4: 'Get these durable floating shelves and organize your bathroom with style',
      5: 'Experience crystal-clear reflection with anti-fog LED lighting and modern elegance',
      6: 'Transform your bathtub into a luxury spa retreat with this premium acacia wood bath caddy',
      7: 'Enjoy warm, cozy towels every day with this luxury towel warmer featuring Bluetooth speaker',
      8: 'Get this premium oak wood construction with tempered glass elegance for your living room',
      9: 'Get this lift-top coffee table with hidden storage and mid-century modern design',
      10: 'Equip your kitchen with this comprehensive 22-piece stainless steel knife set',
      11: 'Add this waterproof, UV-resistant canvas wall art with elegant black frame to your home',
      12: 'Experience high-definition giclee printing on waterproof canvas with professional framing',
      13: 'Get the Govee Floor Lamp 2 with RGBICWW technology and voice control capabilities',
      14: 'Get this powerful 1700W air fryer with 8-in-1 versatility and stylish design',
      15: 'Get this tall bathroom storage cabinet with adjustable shelves and anti-tip design',
      30: 'Experience ergonomic comfort with smooth height adjustment and spacious worksurface',
      31: 'Bring spa-inspired sage green style and complete bathroom organization to your home',
      32: 'Solid wood, USA-made quality with a built-in shelf — the perfect wall-mounted coat rack for any entryway',
      33: 'OEKO-TEX certified goose feather & down fill, 100% cotton shell, and box-stitch construction — upgrade your sleep tonight',
    };
    return descriptions[id] || 'Experience quality and style with this exceptional product';
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={`${review.title} | Interior Decor Solutions`}
        description={review.reviewText.substring(0, 160)}
        keywords={`${review.category}, product review, ${review.title}, home decor review`}
        canonical={`${siteUrl}/reviews/${review.slug}`}
        jsonLd={jsonLd}
      />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="px-4 md:px-8 lg:px-20 h-[70px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <h1 className="text-[#1A4D4D] text-xl md:text-2xl lg:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.02em' }}>
              Interior Decor Solutions
            </h1>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <Link to="/" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">Home</Link>
            <Link to="/blog" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">Blog</Link>
            <Link to="/room-inspiration" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">Room Inspiration</Link>
            <Link to="/reviews" className="text-[#FF6B6B] font-medium text-base whitespace-nowrap">Reviews</Link>
            <Link to="/about" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">About</Link>
          </div>

          <button className="hidden lg:flex bg-[#1A4D4D] text-white px-6 py-2.5 rounded-full font-medium text-sm hover:bg-[#0F3333] transition-all whitespace-nowrap items-center gap-2">
            <i className="ri-mail-line"></i>
            Get Ideas
          </button>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-[#1A4D4D]"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-4 space-y-3">
              <Link to="/" className="block text-[#1A4D4D] font-medium text-base py-2">Home</Link>
              <Link to="/blog" className="block text-[#1A4D4D] font-medium text-base py-2">Blog</Link>
              <Link to="/room-inspiration" className="block text-[#1A4D4D] font-medium text-base py-2">Room Inspiration</Link>
              <Link to="/reviews" className="block text-[#FF6B6B] font-medium text-base py-2">Reviews</Link>
              <Link to="/about" className="block text-[#1A4D4D] font-medium text-base py-2">About</Link>
              <button className="w-full bg-[#1A4D4D] text-white px-6 py-2.5 rounded-full font-medium text-sm hover:bg-[#0F3333] transition-all whitespace-nowrap flex items-center justify-center gap-2">
                <i className="ri-mail-line"></i>
                Get Ideas
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Breadcrumbs */}
      <div className="pt-24 md:pt-32 pb-6 px-4 md:px-8 lg:px-20 mt-[70px] bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-[#FF6B6B] transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/reviews" className="hover:text-[#FF6B6B] transition-colors">Reviews</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-[#1A4D4D] font-medium">{review.category}</span>
          </div>
        </div>
      </div>

      {/* Review Content */}
      <article className="py-8 md:py-12 px-4 md:px-8 lg:px-20 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
            {/* Review Image */}
            <div className="w-full h-[300px] md:h-[500px] bg-gray-100 flex items-center justify-center">
              <img 
                src={review.image}
                alt={review.title}
                className="w-full h-full object-contain"
              />
            </div>

            {/* Review Content */}
            <div className="p-6 md:p-10">
              {/* Category Badge */}
              <span className="inline-block px-4 py-1.5 bg-[#1A4D4D]/10 text-[#1A4D4D] text-xs font-semibold uppercase rounded-full mb-4">
                {review.category}
              </span>

              {/* Title */}
              <h1 className="text-[#1A4D4D] text-3xl md:text-4xl font-bold mb-4">
                {review.title}
              </h1>

              {/* Meta Info */}
              <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <i className="ri-user-line"></i>
                  <span>{review.author}</span>
                </div>
                <span>•</span>
                <span>{review.date}</span>
                {review.verified && (
                  <>
                    <span>•</span>
                    <div className="flex items-center gap-1 text-[#14B8A6]">
                      <i className="ri-checkbox-circle-fill"></i>
                      <span className="font-medium">Verified Purchase</span>
                    </div>
                  </>
                )}
              </div>

              {/* Rating */}
              <div className="flex items-center gap-2 mb-6">
                {[...Array(5)].map((_, i) => (
                  <i 
                    key={i}
                    className={`${i < review.rating ? 'ri-star-fill text-[#FFB800]' : 'ri-star-line text-gray-300'} text-2xl`}
                  ></i>
                ))}
                <span className="text-[#1A4D4D] font-bold text-xl ml-2">{review.rating}</span>
              </div>

              {/* Review Text */}
              <div className="prose prose-lg max-w-none mb-8">
                {(review.fullReview || review.reviewText)?.split('\n').map((paragraph, index) => (
                  paragraph.trim() && (
                    <p key={index} className="text-gray-700 leading-relaxed text-base md:text-lg mb-4">
                      {paragraph}
                    </p>
                  )
                ))}
              </div>

              {/* Pros and Cons */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {/* Pros */}
                <div className="bg-green-50 rounded-xl p-6">
                  <h3 className="text-[#1A4D4D] text-2xl font-bold mb-4 flex items-center gap-2">
                    <i className="ri-thumb-up-fill text-green-600"></i>
                    Pros
                  </h3>
                  <ul className="space-y-2">
                    {review.pros.map((pro, index) => (
                      <li key={index} className="flex items-start gap-2 text-gray-700">
                        <i className="ri-check-line text-green-600 mt-1 flex-shrink-0"></i>
                        <span className="text-sm md:text-base">{pro}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Cons */}
                <div className="bg-orange-50 rounded-xl p-6">
                  <h3 className="text-[#1A4D4D] text-2xl font-bold mb-4 flex items-center gap-2">
                    <i className="ri-thumb-down-fill text-orange-600"></i>
                    Cons
                  </h3>
                  <ul className="space-y-2">
                    {review.cons.map((con, index) => (
                      <li key={index} className="flex items-start gap-2 text-gray-700">
                        <i className="ri-close-line text-orange-600 mt-1 flex-shrink-0"></i>
                        <span className="text-sm md:text-base">{con}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* CTA Button */}
              <div className="bg-[#F9F7F4] rounded-xl p-6 md:p-8 text-center">
                <h3 className="text-[#1A4D4D] text-2xl font-bold mb-3">
                  {getCTATitle(review.id)}
                </h3>
                <p className="text-gray-600 mb-6 text-sm md:text-base">
                  {getCTADescription(review.id)}
                </p>
                <a 
                  href={review.affiliateLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-8 py-4 rounded-full font-semibold text-base hover:bg-[#FF5252] transition-all whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl"
                >
                  <i className="ri-shopping-cart-line text-xl"></i>
                  View on Amazon
                  <i className="ri-arrow-right-line text-xl"></i>
                </a>
                <p className="text-xs text-gray-500 mt-4">
                  As an Amazon Associate, we earn from qualifying purchases at no additional cost to you.
                </p>
              </div>
            </div>
          </div>

          {/* Navigation Between Reviews */}
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {prevReview && (
              <Link 
                to={`/reviews/${prevReview.slug}`}
                className="group bg-white border border-gray-200 rounded-xl p-6 hover:border-[#FF6B6B] transition-all"
              >
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                  <i className="ri-arrow-left-line"></i>
                  <span>Previous Review</span>
                </div>
                <h3 className="text-[#1A4D4D] font-bold text-lg group-hover:text-[#FF6B6B] transition-colors line-clamp-2">
                  {prevReview.title}
                </h3>
              </Link>
            )}
            {nextReview && (
              <Link 
                to={`/reviews/${nextReview.slug}`}
                className="group bg-white border border-gray-200 rounded-xl p-6 hover:border-[#FF6B6B] transition-all md:text-right"
              >
                <div className="flex items-center justify-end gap-2 text-sm text-gray-500 mb-2">
                  <span>Next Review</span>
                  <i className="ri-arrow-right-line"></i>
                </div>
                <h3 className="text-[#1A4D4D] font-bold text-lg group-hover:text-[#FF6B6B] transition-colors line-clamp-2">
                  {nextReview.title}
                </h3>
              </Link>
            )}
          </div>

          {/* Back to All Reviews */}
          <div className="mt-8 text-center">
            <Link 
              to="/reviews"
              className="inline-flex items-center gap-2 text-[#1A4D4D] font-medium hover:text-[#FF6B6B] transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              Back to All Reviews
            </Link>
          </div>
        </div>
      </article>

      {/* Newsletter CTA */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-[#1A4D4D]">
          <div className="absolute inset-0 opacity-10">
            <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
              <pattern id="world-pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                <circle cx="50" cy="50" r="2" fill="white" opacity="0.3"/>
                <circle cx="20" cy="30" r="1.5" fill="white" opacity="0.2"/>
                <circle cx="80" cy="70" r="1.5" fill="white" opacity="0.2"/>
              </pattern>
              <rect width="100%" height="100%" fill="url(#world-pattern)"/>
            </svg>
          </div>
        </div>

        <div className="relative z-10 max-w-2xl mx-auto text-center px-4 md:px-8">
          <h2 className="text-white text-3xl md:text-4xl lg:text-5xl font-light mb-3 md:mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            Never miss a cozy idea
          </h2>
          <p className="text-white/80 text-base md:text-lg mb-6 md:mb-8">
            Weekly decor tips & exclusive deals
          </p>
          
          <form onSubmit={handleNewsletterSubmit} className="flex items-center justify-center gap-0 max-w-sm mx-auto" data-readdy-form id="newsletter-review-detail">
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email"
              required
              className="flex-1 h-[50px] md:h-[54px] px-4 md:px-5 bg-transparent border-2 border-white/40 rounded-l-full text-white placeholder-white/60 focus:outline-none focus:border-white/60 text-sm"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-[50px] h-[50px] md:w-[54px] md:h-[54px] bg-[#1A4D4D] rounded-r-full flex items-center justify-center text-white hover:bg-[#0F3333] transition-all disabled:opacity-50 whitespace-nowrap"
            >
              <i className="ri-notification-line text-lg md:text-xl"></i>
            </button>
          </form>
          {submitMessage && (
            <p className={`mt-4 text-sm ${submitMessage.includes('Thank') ? 'text-white' : 'text-[#FF6B6B]'}`}>
              {submitMessage}
            </p>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="relative bg-[#1A4D4D] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08]">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <pattern id="home-pattern" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
              <path d="M40 20 L50 30 L50 45 L30 45 L30 30 Z" stroke="white" fill="none" strokeWidth="1"/>
              <circle cx="15" cy="60" r="3" stroke="white" fill="none" strokeWidth="1"/>
              <rect x="60" y="55" width="8" height="8" stroke="white" fill="none" strokeWidth="1"/>
            </pattern>
            <rect width="100%" height="100%" fill="url(#home-pattern)"/>
          </svg>
        </div>

        <div className="relative z-10 px-4 md:px-8 lg:px-20 py-12 md:py-20">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-10 gap-8 md:gap-10">
            <div className="lg:col-span-3">
              <div className="flex items-center gap-2 mb-6">
                <img src="https://public.readdy.ai/ai/img_res/9e46e3d9-adac-48a0-871c-356cdcae054d.png" alt="Simple Home Decor" className="h-8 w-auto brightness-0 invert" />
              </div>
              
              <p className="text-white text-2xl md:text-3xl font-light leading-tight mb-8" style={{ fontFamily: 'Georgia, serif' }}>
                Simple touches.<br />
                Cozy homes.
              </p>
            </div>

            <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Shop</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><Link to="/reviews" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Living Room</Link></li>
                  <li><Link to="/reviews" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Bedroom</Link></li>
                  <li><Link to="/reviews" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Kitchen</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Learn</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><Link to="/blog" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Blog</Link></li>
                  <li><Link to="/reviews" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Reviews</Link></li>
                  <li><Link to="/blog" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Guides</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Company</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><Link to="/about" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">About</Link></li>
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Contact</a></li>
                  <li><Link to="/affiliate-disclosure" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Affiliate Disclosure</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Connect</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Pinterest</a></li>
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Instagram</a></li>
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Newsletter</a></li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 border-t border-white/20 px-4 md:px-8 lg:px-20 py-8 md:py-12">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
            <p className="text-white/80 text-xs md:text-sm text-center md:text-left">
              © 2026 Simple Home Decor. All rights reserved.
            </p>

            <div className="flex items-center gap-3 md:gap-4">
              <a href="#" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-pinterest-line text-base md:text-lg"></i>
              </a>
              <a href="#" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-instagram-line text-base md:text-lg"></i>
              </a>
              <a href="#" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-facebook-line text-base md:text-lg"></i>
              </a>
              <a href="https://www.youtube.com/@InteriorDecorSolutions" target="_blank" rel="noopener noreferrer" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-youtube-line text-base md:text-lg"></i>
              </a>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4 text-white/80 text-xs md:text-sm">
              <Link to="/privacy" className="hover:text-white transition-colors whitespace-nowrap">Privacy Policy</Link>
              <span className="hidden md:inline">|</span>
              <Link to="/terms" className="hover:text-white transition-colors whitespace-nowrap">Terms of Use</Link>
              <span className="hidden md:inline">|</span>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors whitespace-nowrap">Web Design</a>
            </div>
          </div>
          
          <div className="max-w-7xl mx-auto mt-6 pt-6 border-t border-white/10">
            <p className="text-white/60 text-xs text-center leading-relaxed">
              Affiliate Disclosure: This website contains affiliate links. We may earn a commission when you make a purchase through these links at no additional cost to you.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
